import { useState, useEffect } from 'react';
import axios from "axios";
import AOS from 'aos';
import 'aos/dist/aos.css';
import '../styles/Navbar.css';
import logo from "../assets/images/logo.png";
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faAngleDown, faHouse, faArrowRightLong, faUserGroup, faBlog, faEnvelope, faGears, faXmark, faPhone } /* faBullhorn: re-add when Swiftlead returns */ from '@fortawesome/free-solid-svg-icons';

const Navbar = () => {
    const location = useLocation();
    const getActiveClass = (path) => location.pathname === path ? 'active' : '';

    const [showGoToSwiftboard, setShowGoToSwiftboard] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
    const [mobileSubOpen, setMobileSubOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const authenticate = async () => {
            try {
                const response = await axios.get("https://api.suryagroup.us/v1/user/get", { withCredentials: true });
                setShowGoToSwiftboard(!!response.data.status);
            } catch {
                setShowGoToSwiftboard(false);
            }
        };
        authenticate();

        const getUserIp = async () => {
            try {
                if (localStorage.getItem("visitSent")) return;
                const ipRes = await axios.get("https://api.ipify.org?format=json");
                const ip = ipRes.data.ip;
                if ("geolocation" in navigator) {
                    navigator.geolocation.getCurrentPosition(
                        (pos) => axios.post("https://api.suryagroup.us/api/visit", { userIp: ip, location: `${pos.coords.latitude},${pos.coords.longitude}` }),
                        () => axios.post("https://api.suryagroup.us/api/visit", { userIp: ip, location: "Blocked" })
                    );
                } else {
                    await axios.post("https://api.suryagroup.us/api/visit", { userIp: ip, location: "Not supported" });
                }
                localStorage.setItem("visitSent", "true");
            } catch (err) {
                console.error("Error tracking visit:", err);
            }
        };
        getUserIp();

        AOS.init({ duration: 1000, once: true });

        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    const closeMobile = () => setMobileOpen(false);

    return (
        <header className={`mainHeader${scrolled ? ' scrolled' : ''}`}>
            <nav role="navigation" aria-label="Main Navigation">

                {/* Top contact bar — desktop only */}
                <div id="top-bar">
                    <div className="top-bar-inner">
                        <div className="top-bar-contacts">
                            <a className="top-link" href="mailto:Info@suryagroup.us">
                                <FontAwesomeIcon icon={faEnvelope} />
                                Info@suryagroup.us
                            </a>
                            <a className="top-link" href="tel:+18163369313">
                                <FontAwesomeIcon icon={faPhone} />
                                +1 (816) 336-9313
                            </a>
                        </div>
                        <a
                            href="https://www.linkedin.com/company/suryagroupestimation"
                            aria-label="LinkedIn"
                            className="social-link"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FontAwesomeIcon icon={faLinkedin} />
                            LinkedIn
                        </a>
                    </div>
                </div>

                {/* Main nav bar */}
                <div className="nav-main">
                    <div className="nav-inner">

                        {/* Logo */}
                        <Link to="/" className="nav-logo">
                            <img width="527" height="504" decoding="async" src={logo} alt="Surya Group" />
                            <span>Surya Group</span>
                        </Link>

                        {/* Desktop nav: left links | center logo | right links */}
                        <div className="desktop-nav">
                            <ul className="desktop-links">
                                <li>
                                    <Link className={`nav-link ${getActiveClass('/')}`} to="/">
                                        <FontAwesomeIcon icon={faHouse} />
                                        Home
                                    </Link>
                                </li>
                                <li>
                                    <Link className={`nav-link ${getActiveClass('/about')}`} to="/about">
                                        <FontAwesomeIcon icon={faUserGroup} />
                                        About
                                    </Link>
                                </li>
                                <li className="has-dropdown">
                                    <span className="nav-link dropdown-trigger">
                                        <FontAwesomeIcon icon={faGears} />
                                        Services
                                        <FontAwesomeIcon icon={faAngleDown} className="drop-arrow" />
                                    </span>
                                    <div className="mega-menu">
                                        <div className="mega-menu-inner">
                                            <div className="mega-col">
                                                <p className="menu-heading">Finishes</p>
                                                <Link className={getActiveClass('/services/flooring-estimation')} to="/services/flooring-estimation">Flooring Estimation</Link>
                                                <Link className={getActiveClass('/services/drywall-estimation')} to="/services/drywall-estimation">Drywall Estimation</Link>
                                                <Link className={getActiveClass('/services/painting-estimation')} to="/services/painting-estimation">Painting Estimation</Link>
                                                <Link className={getActiveClass('/services/siding-estimation')} to="/services/siding-estimation">Siding Estimation</Link>
                                            </div>
                                            <div className="mega-col">
                                                <p className="menu-heading">Structural &amp; Envelope</p>
                                                <Link className={getActiveClass('/services/lumber-estimation')} to="/services/lumber-estimation">Lumber Estimation</Link>
                                                <Link className={getActiveClass('/services/concrete-estimation')} to="/services/concrete-estimation">Concrete Estimation</Link>
                                                <Link className={getActiveClass('/services/masonry-estimation')} to="/services/masonry-estimation">Masonry Estimation</Link>
                                                <Link className={getActiveClass('/services/roofing-estimation')} to="/services/roofing-estimation">Roofing Estimation</Link>
                                                <Link className={getActiveClass('/services/insulation-and-moisture-protection')} to="/services/insulation-and-moisture-protection">Insulation &amp; Moisture</Link>
                                                <Link className={getActiveClass('/services/demolition-estimation')} to="/services/demolition-estimation">Demolition Estimation</Link>
                                            </div>
                                            <div className="mega-col">
                                                <p className="menu-heading">Mechanical, Electrical &amp; Specialty</p>
                                                <Link className={getActiveClass('/services/electrical-estimation')} to="/services/electrical-estimation">Electrical Estimation</Link>
                                                <Link className={getActiveClass('/services/mep-estimation')} to="/services/mep-estimation">MEP Estimation</Link>
                                                <Link className={getActiveClass('/services/openings-and-hardware')} to="/services/openings-and-hardware">Openings &amp; Hardware</Link>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            </ul>

                            {/* Center logo — desktop only */}
                            <Link to="/" className="nav-logo-center">
                                <img width="527" height="504" decoding="async" src={logo} alt="Surya Group" />
                            </Link>

                            <ul className="desktop-links">
                                <li>
                                    <Link className={`nav-link ${getActiveClass('/blog')}`} to="/blog">
                                        <FontAwesomeIcon icon={faBlog} />
                                        Blog
                                    </Link>
                                </li>
                                <li>
                                    <Link className={`nav-link ${getActiveClass('/frequently-asked-questions')}`} to="/frequently-asked-questions">
                                        FAQs
                                    </Link>
                                </li>
                                <li>
                                    <Link className={`nav-link ${getActiveClass('/contact-us')}`} to="/contact-us">
                                        <FontAwesomeIcon icon={faEnvelope} />
                                        Contact
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        {/* Desktop CTA */}
                        <div className="nav-cta">
                            {/* Swiftlead hidden for now — login not available yet
                            <a className="cta-btn cta-swiftreach" href={process.env.REACT_APP_FRONTEND_SWIFTLEAD_URL} target="_blank" rel="noopener noreferrer">
                                <FontAwesomeIcon icon={faBullhorn} />
                                Swiftlead
                            </a>
                            */}
                            {showGoToSwiftboard ? (
                                <a className="cta-btn cta-swiftboard" href="https://swiftboard.suryagroup.us">
                                    Swiftboard <FontAwesomeIcon icon={faArrowRightLong} />
                                </a>
                            ) : (
                                <Link className="cta-btn cta-login" to="/login">
                                    Log in
                                </Link>
                            )}
                            <Link className="cta-btn cta-demo" to="/schedule-meeting">
                                Book a demo
                            </Link>
                        </div>

                        {/* Hamburger */}
                        <button
                            className={`hamburger${mobileOpen ? ' is-open' : ''}`}
                            onClick={() => setMobileOpen(!mobileOpen)}
                            aria-label="Toggle navigation menu"
                            aria-expanded={mobileOpen}
                        >
                            <span></span>
                            <span></span>
                            <span></span>
                        </button>
                    </div>
                </div>

                {/* Mobile drawer overlay */}
                <div
                    className={`mobile-overlay${mobileOpen ? ' active' : ''}`}
                    onClick={closeMobile}
                    aria-hidden={!mobileOpen}
                >
                    <div className="mobile-drawer" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">

                        <div className="drawer-header">
                            <Link to="/" onClick={closeMobile} className="drawer-logo">
                                <img width="527" height="504" decoding="async" src={logo} alt="Surya Group" />
                                <span>Surya Group</span>
                            </Link>
                            <button className="drawer-close" onClick={closeMobile} aria-label="Close menu">
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>

                        <div className="drawer-body">
                            <Link className={`drawer-link${location.pathname === '/' ? ' active' : ''}`} to="/" onClick={closeMobile}>
                                <FontAwesomeIcon icon={faHouse} />
                                Home
                            </Link>
                            <Link className={`drawer-link${location.pathname === '/about' ? ' active' : ''}`} to="/about" onClick={closeMobile}>
                                <FontAwesomeIcon icon={faUserGroup} />
                                About
                            </Link>

                            {/* Services accordion */}
                            <div className="drawer-accordion">
                                <button
                                    className={`drawer-accordion-btn${mobileServicesOpen ? ' open' : ''}`}
                                    onClick={() => setMobileServicesOpen(v => !v)}
                                >
                                    <span><FontAwesomeIcon icon={faGears} /> Services</span>
                                    <FontAwesomeIcon icon={faAngleDown} className="acc-arrow" />
                                </button>
                                {mobileServicesOpen && (
                                    <div className="drawer-accordion-content">
                                        <button
                                            className={`drawer-sub-btn${mobileSubOpen ? ' open' : ''}`}
                                            onClick={() => setMobileSubOpen(v => !v)}
                                        >
                                            <span>Sub-Contractor</span>
                                            <FontAwesomeIcon icon={faAngleDown} className="acc-arrow" />
                                        </button>
                                        {mobileSubOpen && (
                                            <div className="drawer-sub-content">
                                                <p className="menu-heading">Finishes</p>
                                                <Link to="/services/flooring-estimation" onClick={closeMobile}>Flooring Estimation</Link>
                                                <Link to="/services/drywall-estimation" onClick={closeMobile}>Drywall Estimation</Link>
                                                <Link to="/services/painting-estimation" onClick={closeMobile}>Painting Estimation</Link>
                                                <Link to="/services/siding-estimation" onClick={closeMobile}>Siding Estimation</Link>
                                                <p className="menu-heading">Structural &amp; Envelope</p>
                                                <Link to="/services/lumber-estimation" onClick={closeMobile}>Lumber Estimation</Link>
                                                <Link to="/services/concrete-estimation" onClick={closeMobile}>Concrete Estimation</Link>
                                                <Link to="/services/masonry-estimation" onClick={closeMobile}>Masonry Estimation</Link>
                                                <Link to="/services/roofing-estimation" onClick={closeMobile}>Roofing Estimation</Link>
                                                <Link to="/services/insulation-and-moisture-protection" onClick={closeMobile}>Insulation &amp; Moisture</Link>
                                                <Link to="/services/demolition-estimation" onClick={closeMobile}>Demolition Estimation</Link>
                                                <p className="menu-heading">Mechanical, Electrical &amp; Specialty</p>
                                                <Link to="/services/electrical-estimation" onClick={closeMobile}>Electrical Estimation</Link>
                                                <Link to="/services/mep-estimation" onClick={closeMobile}>MEP Estimation</Link>
                                                <Link to="/services/openings-and-hardware" onClick={closeMobile}>Openings &amp; Hardware</Link>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <Link className={`drawer-link${location.pathname === '/blog' ? ' active' : ''}`} to="/blog" onClick={closeMobile}>
                                <FontAwesomeIcon icon={faBlog} />
                                Blog
                            </Link>
                            <Link className={`drawer-link${location.pathname === '/frequently-asked-questions' ? ' active' : ''}`} to="/frequently-asked-questions" onClick={closeMobile}>
                                FAQs
                            </Link>
                            <Link className={`drawer-link${location.pathname === '/contact-us' ? ' active' : ''}`} to="/contact-us" onClick={closeMobile}>
                                <FontAwesomeIcon icon={faEnvelope} />
                                Contact Us
                            </Link>
                        </div>

                        <div className="drawer-footer">
                            <div className="drawer-contacts">
                                <a href="mailto:Info@suryagroup.us" className="drawer-contact-link">
                                    <FontAwesomeIcon icon={faEnvelope} />
                                    Info@suryagroup.us
                                </a>
                                <a href="tel:+18163369313" className="drawer-contact-link">
                                    <FontAwesomeIcon icon={faPhone} />
                                    +1 (816) 336-9313
                                </a>
                            </div>
                            <div className="drawer-portal-btns">
                                {/* Swiftlead hidden for now — login not available yet
                                <a className="drawer-cta drawer-cta-swiftreach" href={process.env.REACT_APP_FRONTEND_SWIFTLEAD_URL} target="_blank" rel="noopener noreferrer">
                                    <FontAwesomeIcon icon={faBullhorn} />
                                    SwiftReach Login
                                </a>
                                */}
                                <Link className="drawer-cta drawer-cta-demo" to="/schedule-meeting" onClick={closeMobile}>
                                    Book a demo
                                </Link>
                                {showGoToSwiftboard ? (
                                    <a className="drawer-cta" href="https://swiftboard.suryagroup.us">
                                        Go to Swiftboard <FontAwesomeIcon icon={faArrowRightLong} />
                                    </a>
                                ) : (
                                    <Link className="drawer-cta" to="/login" onClick={closeMobile}>
                                        Log in
                                    </Link>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

            </nav>
        </header>
    );
};

export default Navbar;
